/** @format */

import {ROLL_SIZE_TYPE_DIAMETER} from 'frontier-constants';
import SuperAgent from 'superagent';
import {getSubstrateThicknesses} from './substrate_thicknesses';

const calcDiameterFromQty = async ({
    qty,
    length,
    isHighOpacityWhite,
    thickness = null,
    orderId = null
}) => {
    const runLength = Math.ceil(parseInt(qty) * (length + 0.125));

    // If thickness is provided directly, use it
    if (thickness !== null) {
        const coreRadius = 1.625; // Outer Core Diameter
        return (
            Math.round(
                (Math.sqrt((runLength * thickness) / Math.PI + Math.pow(coreRadius, 2)) * 2) / 0.5
            ) * 0.5
        );
    }

    // We must have an order ID to get the thickness
    if (!orderId) {
        console.error('Either thickness or orderId must be provided');
        return 0;
    }

    // Get thicknesses for the order (includes laminate)
    const thicknesses = await getSubstrateThicknesses(orderId);

    // Since we're using order-based thickness, we just need the first thickness
    // All substrates will have the same thickness for a given order
    if (!thicknesses || thicknesses.length === 0) {
        console.log('No thickness data available for this order');
        return 0;
    }

    const orderThickness = thicknesses[0];
    let T = orderThickness.default;

    if (isHighOpacityWhite && orderThickness.withHighOpacityWhite) {
        T = orderThickness.withHighOpacityWhite;
    }

    const coreRadius = 1.625; // Outer Core Diameter
    return (
        Math.round((Math.sqrt((runLength * T) / Math.PI + Math.pow(coreRadius, 2)) * 2) / 0.5) * 0.5
    );
    // =SQRT(RunLength * MaterialThickness / PI() + CoreDiameter^2) * 2
};

/**
 *
 * @param diameter - Cannot be less than the size of the core
 * @param length
 * @param isHighOpacityWhite
 * @param thickness - Optional thickness value to use directly
 * @param orderId - Order ID to get thickness that includes laminate
 * @returns {Promise<number>|number}
 */
const calcQtyFromDiameter = async ({
    diameter,
    length,
    isHighOpacityWhite,
    thickness = null,
    orderId = null
}) => {
    // If thickness is provided directly, use it
    if (thickness !== null) {
        const core = Math.pow(1.625, 2) * Math.PI;
        const runLength = Math.ceil(
            (Math.pow(diameter, 2) * Math.PI) / (4 * thickness) - core / thickness
        );
        return Math.round(Math.ceil(runLength / (length + 0.125)) / 10) * 10;
    }

    // We must have an order ID to get the thickness
    if (!orderId) {
        console.error('Either thickness or orderId must be provided');
        return 0;
    }

    // Get thicknesses for the order (includes laminate)
    const thicknesses = await getSubstrateThicknesses(orderId);

    // Since we're using order-based thickness, we just need the first thickness
    // All substrates will have the same thickness for a given order
    if (!thicknesses || thicknesses.length === 0) {
        console.log('No thickness data available for this order');
        return 0;
    }

    const orderThickness = thicknesses[0];
    let T = orderThickness.default;

    if (isHighOpacityWhite && orderThickness.withHighOpacityWhite) {
        T = orderThickness.withHighOpacityWhite;
    }

    const core = Math.pow(1.625, 2) * Math.PI;
    const runLength = Math.ceil((Math.pow(diameter, 2) * Math.PI) / (4 * T) - core / T);

    return Math.round(Math.ceil(runLength / (length + 0.125)) / 10) * 10;
};

/**
 * Calculate the number of cores needed based on various parameters
 *
 * @param {Object} params - Parameters for core calculation
 * @param {number} params.totalQty - Total quantity of labels
 * @param {number} params.designQty - Number of unique designs
 * @param {number} params.length - Length of the label
 * @param {number} params.substrateId - Substrate ID
 * @param {number} params.cellsAcross - Number of cells across
 * @param {boolean} [params.isHighOpacityWhite=false] - Whether high opacity white is used
 * @param {boolean} [params.isFewestPossibleRolls=false] - Whether to use fewest possible rolls
 * @param {boolean} [params.isSpecialRollQty=false] - Whether special roll quantity is used
 * @param {number} [params.rollMax=13] - Maximum roll diameter or quantity
 * @param {number} [params.rollTypeId=ROLL_SIZE_TYPE_DIAMETER] - Roll type ID
 * @param {boolean} [params.hasDesignCount=true] - Whether to consider design count in calculation
 * @returns {Promise<{coreCount: number, maxQtyPerRoll: number, rollsPerColumn: number}>}
 */
const calcCores = async params => {
    try {
        const {
            totalQty,
            designQty,
            length,
            substrateId,
            cellsAcross,
            isHighOpacityWhite = false,
            isFewestPossibleRolls = false,
            isSpecialRollQty = false,
            rollMax = 13,
            rollTypeId = ROLL_SIZE_TYPE_DIAMETER,
            hasDesignCount = true
        } = params;

        const response = await SuperAgent.post('/api/orders/calculate-cores')
            .set('Accept', 'application/json')
            .send({
                totalQty,
                designQty,
                length,
                substrateId,
                cellsAcross,
                isHighOpacityWhite,
                isFewestPossibleRolls,
                isSpecialRollQty,
                rollMax,
                rollTypeId,
                hasDesignCount
            });

        return response.body.result;
    } catch (error) {
        console.error('Error calculating cores:', error);
        // Fallback to a simple calculation if the API call fails
        const {totalQty, designQty, cellsAcross, hasDesignCount = true} = params;
        const rollsPerColumn = Math.ceil(totalQty / 1000); // Simple fallback assuming 1000 labels per roll
        const totalCores = rollsPerColumn * cellsAcross;

        return {
            coreCount:
                hasDesignCount && designQty > 0 ? Math.max(totalCores, designQty) : totalCores,
            maxQtyPerRoll: 1000,
            rollsPerColumn
        };
    }
};

// Create synchronous versions of the functions for backward compatibility
// Note: These sync versions can ONLY use thickness directly, not orderId
const calcDiameterFromQtySync = ({qty, length, isHighOpacityWhite, thickness = null}) => {
    if (thickness === null) {
        console.error('Thickness must be provided for sync version');
        return 0;
    }

    const runLength = Math.ceil(parseInt(qty) * (length + 0.125));
    const coreRadius = 1.625; // Outer Core Diameter

    return (
        Math.round(
            (Math.sqrt((runLength * thickness) / Math.PI + Math.pow(coreRadius, 2)) * 2) / 0.5
        ) * 0.5
    );
};

const calcQtyFromDiameterSync = ({diameter, length, isHighOpacityWhite, thickness = null}) => {
    if (thickness === null) {
        console.error('Thickness must be provided for sync version');
        return 0;
    }

    const core = Math.pow(1.625, 2) * Math.PI;
    const runLength = Math.ceil(
        (Math.pow(diameter, 2) * Math.PI) / (4 * thickness) - core / thickness
    );

    return Math.round(Math.ceil(runLength / (length + 0.125)) / 10) * 10;
};

// Export both async and sync versions
export {
    calcCores,
    calcQtyFromDiameter,
    calcDiameterFromQty,
    calcQtyFromDiameterSync,
    calcDiameterFromQtySync
};
